import * as XLSX from 'xlsx';
import { WorkEntry } from '../types';
import { MaterialConsumption } from '../types/material';
import { materialConsumptionApi } from '../api/materialApi';

/**
 * Utility functions for exporting data to CSV and Excel formats
 */

/**
 * Calculate work hours for a work entry
 */
export function calculateWorkHours(entry: WorkEntry): number {
  if (!entry.startTime) return 0;
  
  const start = new Date(entry.startTime);
  const end = entry.endTime ? new Date(entry.endTime) : new Date();
  const workMinutes = Math.floor((end.getTime() - start.getTime()) / (1000 * 60));
  const actualWorkMinutes = Math.max(0, workMinutes - (entry.totalBreakMinutes || 0));
  
  return Number((actualWorkMinutes / 60).toFixed(2));
}

/**
 * Format hours as string for display
 */
export function formatWorkHours(hours: number): string {
  return hours.toFixed(2);
}

/**
 * Export work entries to CSV format
 */
export function exportToCSV(entries: WorkEntry[], filename: string) {
  const csvContent = [
    ['Nume', 'Proiect', 'Locație', 'Data', 'Început', 'Sfârșit', 'Ore lucrate', 'Pauză Total (min)', 'Comentarii', 'Descriere', 'Status'].join(','),
    ...entries.map(entry => [
      `"${entry.userName || 'N/A'}"`,
      `"${entry.projectName || 'N/A'}"`,
      `"${entry.locationName || 'N/A'}"`,
      `"${entry.date || 'N/A'}"`,
      `"${entry.startTime ? new Date(entry.startTime).toLocaleString('ro-RO') : 'N/A'}"`,
      `"${entry.endTime ? new Date(entry.endTime).toLocaleString('ro-RO') : 'În lucru'}"`,
      `"${formatWorkHours(calculateWorkHours(entry))}"`,
      `"${entry.totalBreakMinutes || 0}"`,
      `"${entry.comments ? entry.comments.replace(/"/g, '""').replace(/\n/g, ' | ') : 'N/A'}"`,
      `"${entry.workDescription ? entry.workDescription.replace(/"/g, '""').replace(/\n/g, ' | ') : 'N/A'}"`,
      `"${entry.isActive ? 'Activ' : 'Finalizat'}"`
    ].join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

/**
 * Export group password creation results to Excel format
 */
export async function exportGroupPasswordResults(
  createdUsers: Array<{name: string, role: string, workerType?: string, password: string}>, 
  filename: string
) {
  const workbook = XLSX.utils.book_new();
  
  // Create worksheet with user credentials
  const userData = createdUsers.map(user => ({
    'Nume Utilizator': user.name,
    'Rol': user.role === 'worker' ? 'Muncitor' : 
           user.role === 'supervisor' ? 'Supervisor' : 
           user.role === 'monitor' ? 'Monitor' : user.role,
    'Tip Lucrător': user.workerType || 'N/A',
    'Parolă': user.password,
    'Data Creării': new Date().toLocaleDateString('ro-RO'),
    'Observații': 'Utilizator creat cu parolă de grup'
  }));
  
  const worksheet = XLSX.utils.json_to_sheet(userData);
  
  // Set column widths for better readability
  const colWidths = [
    { wch: 20 }, // Nume Utilizator
    { wch: 15 }, // Rol
    { wch: 15 }, // Tip Lucrător  
    { wch: 12 }, // Parolă
    { wch: 12 }, // Data Creării
    { wch: 30 }  // Observații
  ];
  worksheet['!cols'] = colWidths;
  
  // Add header styling
  const headerStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "366092" } },
    alignment: { horizontal: "center" }
  };
  
  // Apply header styling to first row
  const headerCells = ['A1', 'B1', 'C1', 'D1', 'E1', 'F1'];
  headerCells.forEach(cell => {
    if (worksheet[cell]) {
      worksheet[cell].s = headerStyle;
    }
  });
  
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Utilizatori Creați');
  
  // Add summary sheet
  const roleGroups = createdUsers.reduce((groups, user) => {
    const roleDisplay = user.role === 'worker' ? 'Muncitori' : 
                       user.role === 'supervisor' ? 'Supervisori' : 
                       user.role === 'monitor' ? 'Monitori' : user.role;
    groups[roleDisplay] = (groups[roleDisplay] || 0) + 1;
    return groups;
  }, {} as Record<string, number>);
  
  const summaryData = [
    { 'Statistici Creare Grup': 'Numărul total de utilizatori creați', 'Valoare': createdUsers.length },
    { 'Statistici Creare Grup': 'Data creării', 'Valoare': new Date().toLocaleString('ro-RO') },
    { 'Statistici Creare Grup': 'Parola comună folosită', 'Valoare': createdUsers[0]?.password || 'N/A' },
    ...Object.entries(roleGroups).map(([role, count]) => ({
      'Statistici Creare Grup': `Utilizatori ${role}`,
      'Valoare': count
    }))
  ];
  
  const summarySheet = XLSX.utils.json_to_sheet(summaryData);
  summarySheet['!cols'] = [{ wch: 30 }, { wch: 15 }];
  
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Sumar Creare');
  
  // Save file
  XLSX.writeFile(workbook, filename);
}

/**
 * Export bulk import results to Excel format with usernames and passwords
 */
export async function exportBulkImportResults(createdUsers: Array<{name: string, workerType: string, password: string}>, filename: string) {
  const workbook = XLSX.utils.book_new();
  
  // Create worksheet with user credentials
  const userData = createdUsers.map(user => ({
    'Nume Utilizator': user.name,
    'Tip Lucrător': user.workerType,
    'Parolă': user.password,
    'Data Creării': new Date().toLocaleDateString('ro-RO'),
    'Observații': 'Utilizator creat prin import masiv'
  }));
  
  const worksheet = XLSX.utils.json_to_sheet(userData);
  
  // Set column widths for better readability
  const colWidths = [
    { wch: 20 }, // Nume Utilizator
    { wch: 15 }, // Tip Lucrător  
    { wch: 12 }, // Parolă
    { wch: 12 }, // Data Creării
    { wch: 25 }  // Observații
  ];
  worksheet['!cols'] = colWidths;
  
  // Add header styling
  const headerStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "366092" } },
    alignment: { horizontal: "center" }
  };
  
  // Apply header styling to first row
  const headerCells = ['A1', 'B1', 'C1', 'D1', 'E1'];
  headerCells.forEach(cell => {
    if (worksheet[cell]) {
      worksheet[cell].s = headerStyle;
    }
  });
  
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Utilizatori Creați');
  
  // Add summary sheet
  const summaryData = [
    { 'Statistici Import': 'Numărul total de utilizatori creați', 'Valoare': createdUsers.length },
    { 'Statistici Import': 'Data importului', 'Valoare': new Date().toLocaleString('ro-RO') },
    { 'Statistici Import': 'Utilizatori Angajați', 'Valoare': createdUsers.filter(u => u.workerType === 'Angajat').length },
    { 'Statistici Import': 'Utilizatori Colaboratori', 'Valoare': createdUsers.filter(u => u.workerType === 'Colaborator').length }
  ];
  
  const summarySheet = XLSX.utils.json_to_sheet(summaryData);
  summarySheet['!cols'] = [{ wch: 30 }, { wch: 15 }];
  
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Sumar Import');
  
  // Save file
  XLSX.writeFile(workbook, filename);
}

/**
 * Export work entries to Excel format with comprehensive data
 */
export async function exportToExcel(entries: WorkEntry[], filename: string) {
  const workbook = XLSX.utils.book_new();
  
  // Main work entries sheet with all data
  const workData = entries.map(entry => ({
    'Nume': entry.userName || 'N/A',
    'Proiect': entry.projectName || 'N/A',
    'Locație': entry.locationName || 'N/A',
    'Data': entry.date || 'N/A',
    'Început': entry.startTime ? new Date(entry.startTime).toLocaleString('ro-RO') : 'N/A',
    'Sfârșit': entry.endTime ? new Date(entry.endTime).toLocaleString('ro-RO') : 'În lucru',
    'Ore lucrate': formatWorkHours(calculateWorkHours(entry)),
    'Pauză Total (min)': entry.totalBreakMinutes || 0,
    'Comentarii': entry.comments || 'N/A',
    'Descriere': entry.workDescription || 'N/A',
    'Status': entry.isActive ? 'Activ' : 'Finalizat',
    'ID Intrare': entry.id
  }));

  const workSheet = XLSX.utils.json_to_sheet(workData);
  
  // Set column widths for better readability
  workSheet['!cols'] = [
    { width: 20 }, // Nume
    { width: 25 }, // Proiect
    { width: 25 }, // Locație
    { width: 12 }, // Data
    { width: 18 }, // Început
    { width: 18 }, // Sfârșit
    { width: 12 }, // Ore lucrate
    { width: 15 }, // Pauză Total
    { width: 30 }, // Comentarii
    { width: 30 }, // Descriere
    { width: 10 }, // Status
    { width: 10 }  // ID
  ];
  
  XLSX.utils.book_append_sheet(workbook, workSheet, 'Pontaj');

  // Breaks sheet with detailed break information
  const breaksData: Record<string, unknown>[] = [];
  entries.forEach(entry => {
    if (entry.breaks && entry.breaks.length > 0) {
      entry.breaks.forEach(br => {
        let durationMinutes = 0;
        if (br.endTime && br.startTime) {
          durationMinutes = Math.round((new Date(br.endTime).getTime() - new Date(br.startTime).getTime()) / (1000 * 60));
        }
        
        breaksData.push({
          'Nume': entry.userName || 'N/A',
          'Data': entry.date || 'N/A',
          'Proiect': entry.projectName || 'N/A',
          'Locație': entry.locationName || 'N/A',
          'Început pauză': new Date(br.startTime).toLocaleString('ro-RO'),
          'Sfârșit pauză': br.endTime ? new Date(br.endTime).toLocaleString('ro-RO') : 'În curs',
          'Durata (min)': durationMinutes,
          'Motiv': br.reason || 'N/A',
          'Status': br.isActive ? 'Activ' : 'Finalizat',
          'ID Break': br.id
        });
      });
    }
  });

  if (breaksData.length > 0) {
    const breaksSheet = XLSX.utils.json_to_sheet(breaksData);
    breaksSheet['!cols'] = [
      { width: 20 }, // Nume
      { width: 12 }, // Data
      { width: 25 }, // Proiect
      { width: 25 }, // Locație
      { width: 18 }, // Început pauză
      { width: 18 }, // Sfârșit pauză
      { width: 12 }, // Durata
      { width: 30 }, // Motiv
      { width: 10 }, // Status
      { width: 10 }  // ID
    ];
    XLSX.utils.book_append_sheet(workbook, breaksSheet, 'Pauze');
  }

  // Load and include material consumption data
  const materialsData: Record<string, unknown>[] = [];
  for (const entry of entries) {
    try {
      const materials = await materialConsumptionApi.getByWorkEntry(entry.id);
      const materialsArray = Array.isArray(materials) ? materials : [];
      
      materialsArray.forEach(material => {
        materialsData.push({
          'Nume': entry.userName || 'N/A',
          'Data': entry.date || 'N/A',
          'Proiect': entry.projectName || 'N/A',
          'Locație': entry.locationName || 'N/A',
          'Material': material.materialTypeName || 'N/A',
          'Cantitate': material.quantity || 0,
          'Unitate': material.unitSymbol || material.unitName || 'N/A',
          'Adăugat de': material.addedByUserName || 'N/A',
          'Adăugat pentru': material.addedForUserName || 'N/A',
          'Data adăugare': material.createdAt ? new Date(material.createdAt).toLocaleString('ro-RO') : 'N/A',
          'Notițe': material.notes || 'N/A',
          'ID Material': material.id
        });
      });
    } catch (error) {
      console.warn(`Error loading materials for work entry ${entry.id}:`, error);
    }
  }

  if (materialsData.length > 0) {
    const materialsSheet = XLSX.utils.json_to_sheet(materialsData);
    materialsSheet['!cols'] = [
      { width: 20 }, // Nume
      { width: 12 }, // Data
      { width: 25 }, // Proiect
      { width: 25 }, // Locație
      { width: 25 }, // Material
      { width: 10 }, // Cantitate
      { width: 10 }, // Unitate
      { width: 20 }, // Adăugat de
      { width: 20 }, // Adăugat pentru
      { width: 18 }, // Data adăugare
      { width: 30 }, // Notițe
      { width: 10 }  // ID
    ];
    XLSX.utils.book_append_sheet(workbook, materialsSheet, 'Materiale');
  }

  // Enhanced summary sheet with material consumption
  const summary = entries.reduce((acc, entry) => {
    const hours = calculateWorkHours(entry);
    const user = acc.find(u => u.nume === entry.userName);
    if (user) {
      user.oreLucrate += hours;
      user.totalPauze += entry.totalBreakMinutes || 0;
      user.intrari += 1;
    } else {
      acc.push({
        nume: entry.userName || 'N/A',
        oreLucrate: hours,
        totalPauze: entry.totalBreakMinutes || 0,
        intrari: 1,
        materialsSummary: [] // Will be populated below
      });
    }
    return acc;
  }, [] as Record<string, unknown>[]);

  // Load and aggregate materials for each user
  for (const summaryUser of summary) {
    const userEntries = entries.filter(entry => entry.userName === summaryUser.nume);
    const userMaterials: Record<string, unknown>[] = [];
    
    for (const entry of userEntries) {
      try {
        const materials = await materialConsumptionApi.getByWorkEntry(entry.id);
        const materialsArray = Array.isArray(materials) ? materials : [];
        userMaterials.push(...materialsArray);
      } catch (error) {
        console.warn(`Error loading materials for entry ${entry.id}:`, error);
      }
    }
    
    // Group materials by type and sum quantities
    const materialGroups = userMaterials.reduce((groups, material) => {
      const key = material.materialTypeName || 'N/A';
      if (!groups[key]) {
        groups[key] = {
          materialType: key,
          totalQuantity: 0,
          unit: material.unitSymbol || material.unitName || '',
          occurrences: 0
        };
      }
      groups[key].totalQuantity += material.quantity || 0;
      groups[key].occurrences += 1;
      return groups;
    }, {} as Record<string, Record<string, { quantity: number; unit: string }>>);
    
    // Create concatenated summary string
    const materialSummaryText = Object.values(materialGroups).map((group: { quantity: number; unit: string }) => 
      `${group.materialType}: ${group.totalQuantity.toLocaleString('ro-RO')} ${group.unit}${group.occurrences > 1 ? ` (${group.occurrences}x)` : ''}`
    ).join('; ');
    
    summaryUser.materialsSummary = materialSummaryText || 'Fără materiale';
  };

  const summarySheet = XLSX.utils.json_to_sheet(summary.map(s => ({
    'Nume': s.nume,
    'Total ore lucrate': formatWorkHours(s.oreLucrate),
    'Total pauze (min)': s.totalPauze,
    'Număr intrări': s.intrari,
    'Media ore/intrare': formatWorkHours(s.oreLucrate / s.intrari),
    'Eficiență (%)': s.oreLucrate > 0 ? Math.round((s.oreLucrate / ((s.oreLucrate * 60) + s.totalPauze) * 60) * 100) : 0,
    'Materiale utilizate': s.materialsSummary
  })));
  
  summarySheet['!cols'] = [
    { width: 20 }, // Nume
    { width: 15 }, // Total ore
    { width: 15 }, // Total pauze
    { width: 12 }, // Număr intrări
    { width: 12 }, // Media ore
    { width: 12 }, // Eficiență
    { width: 50 }  // Materiale utilizate
  ];
  
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Sumar');

  XLSX.writeFile(workbook, filename);
}

/**
 * Export team summary data to Excel
 */
export function exportTeamSummaryToExcel(teamData: Record<string, unknown>[], filename: string) {
  const workbook = XLSX.utils.book_new();
  
  const summaryData = teamData.map(member => ({
    'Nume': member.name,
    'Echipa': member.teamName || 'N/A',
    'Status': member.isWorking ? 'La lucru' : 'Inactiv',
    'În pauză': member.onBreak ? 'Da' : 'Nu',
    'Ore astăzi': member.todayHours?.toFixed(2) || '0.00'
  }));

  const sheet = XLSX.utils.json_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, sheet, 'Echipe');

  XLSX.writeFile(workbook, filename);
}

/**
 * Export final end-of-day report to Excel with complete data
 */
export async function exportFinalReportToExcel(
  reportData: Record<string, unknown>[], 
  workEntries: WorkEntry[], 
  filename: string,
  additionalMaterials?: { supervisorMaterials?: Record<string, unknown>[], teamMaterials?: Record<string, unknown>[] }
) {
  const workbook = XLSX.utils.book_new();
  
  // Main summary report sheet (the one generated by EndOfDayWizard)
  const summarySheet = XLSX.utils.json_to_sheet(reportData);
  summarySheet['!cols'] = [
    { width: 20 }, // Nume
    { width: 15 }, // Rol
    { width: 12 }, // Data
    { width: 15 }, // Ore Lucrate
    { width: 12 }, // Ore Numerice
    { width: 15 }, // Status Final
    { width: 10 }, // Overtime
    { width: 12 }  // Ore Overtime
  ];
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Raport Final');

  // Detailed work entries sheet
  if (workEntries && workEntries.length > 0) {
    const workData = workEntries.map(entry => ({
      'Nume': entry.userName || 'N/A',
      'Proiect': entry.projectName || 'N/A',
      'Locație': entry.locationName || 'N/A',
      'Data': entry.date || 'N/A',
      'Început': entry.startTime ? new Date(entry.startTime).toLocaleString('ro-RO') : 'N/A',
      'Sfârșit': entry.endTime ? new Date(entry.endTime).toLocaleString('ro-RO') : 'În lucru',
      'Ore lucrate': formatWorkHours(calculateWorkHours(entry)),
      'Pauză Total (min)': entry.totalBreakMinutes || 0,
      'Comentarii': entry.comments || 'N/A',
      'Descriere': entry.workDescription || 'N/A',
      'Status': entry.isActive ? 'Activ' : 'Finalizat'
    }));

    const workSheet = XLSX.utils.json_to_sheet(workData);
    workSheet['!cols'] = [
      { width: 20 }, // Nume
      { width: 25 }, // Proiect
      { width: 25 }, // Locație
      { width: 12 }, // Data
      { width: 18 }, // Început
      { width: 18 }, // Sfârșit
      { width: 12 }, // Ore lucrate
      { width: 15 }, // Pauză Total
      { width: 30 }, // Comentarii
      { width: 30 }, // Descriere
      { width: 10 }  // Status
    ];
    XLSX.utils.book_append_sheet(workbook, workSheet, 'Detalii Pontaj');

    // Load and include material consumption data
    const materialsData: any[] = [];
    for (const entry of workEntries) {
      try {
        const materials = await materialConsumptionApi.getByWorkEntry(entry.id);
        const materialsArray = Array.isArray(materials) ? materials : [];
        
        materialsArray.forEach(material => {
          materialsData.push({
            'Nume': entry.userName || 'N/A',
            'Data': entry.date || 'N/A',
            'Proiect': entry.projectName || 'N/A',
            'Locație': entry.locationName || 'N/A',
            'Material': material.materialTypeName || 'N/A',
            'Cantitate': material.quantity || 0,
            'Unitate': material.unitSymbol || material.unitName || 'N/A',
            'Adăugat de': material.addedByUserName || 'N/A',
            'Adăugat pentru': material.addedForUserName || 'N/A',
            'Data adăugare': material.createdAt ? new Date(material.createdAt).toLocaleString('ro-RO') : 'N/A',
            'Notițe': material.notes || 'N/A'
          });
        });
      } catch (error) {
        console.warn(`Error loading materials for work entry ${entry.id}:`, error);
      }
    }

    // Add any additional materials from the wizard
    if (additionalMaterials?.supervisorMaterials) {
      additionalMaterials.supervisorMaterials.forEach(material => {
        materialsData.push({
          'Nume': 'Supervisor',
          'Data': new Date().toISOString().split('T')[0],
          'Proiect': 'Manual Entry',
          'Locație': 'Manual Entry',
          'Material': material.materialType || 'N/A',
          'Cantitate': material.quantity || 0,
          'Unitate': material.unit || 'N/A',
          'Adăugat de': 'Supervisor',
          'Adăugat pentru': 'Supervisor',
          'Data adăugare': new Date().toLocaleString('ro-RO'),
          'Notițe': material.notes || 'Material supervisor din wizard final'
        });
      });
    }

    if (additionalMaterials?.teamMaterials) {
      additionalMaterials.teamMaterials.forEach(material => {
        materialsData.push({
          'Nume': 'Echipa',
          'Data': new Date().toISOString().split('T')[0],
          'Proiect': 'Manual Entry',
          'Locație': 'Manual Entry',
          'Material': material.materialType || 'N/A',
          'Cantitate': material.quantity || 0,
          'Unitate': material.unit || 'N/A',
          'Adăugat de': 'Supervisor',
          'Adăugat pentru': 'Echipa',
          'Data adăugare': new Date().toLocaleString('ro-RO'),
          'Notițe': material.notes || 'Material echipă din wizard final'
        });
      });
    }

    if (materialsData.length > 0) {
      const materialsSheet = XLSX.utils.json_to_sheet(materialsData);
      materialsSheet['!cols'] = [
        { width: 20 }, // Nume
        { width: 12 }, // Data
        { width: 25 }, // Proiect
        { width: 25 }, // Locație
        { width: 25 }, // Material
        { width: 10 }, // Cantitate
        { width: 10 }, // Unitate
        { width: 20 }, // Adăugat de
        { width: 20 }, // Adăugat pentru
        { width: 18 }, // Data adăugare
        { width: 30 }  // Notițe
      ];
      XLSX.utils.book_append_sheet(workbook, materialsSheet, 'Materiale');
    }

    // Breaks sheet with detailed break information
    const breaksData: any[] = [];
    workEntries.forEach(entry => {
      if (entry.breaks && entry.breaks.length > 0) {
        entry.breaks.forEach(br => {
          let durationMinutes = 0;
          if (br.endTime && br.startTime) {
            durationMinutes = Math.round((new Date(br.endTime).getTime() - new Date(br.startTime).getTime()) / (1000 * 60));
          }
          
          breaksData.push({
            'Nume': entry.userName || 'N/A',
            'Data': entry.date || 'N/A',
            'Proiect': entry.projectName || 'N/A',
            'Locație': entry.locationName || 'N/A',
            'Început pauză': new Date(br.startTime).toLocaleString('ro-RO'),
            'Sfârșit pauză': br.endTime ? new Date(br.endTime).toLocaleString('ro-RO') : 'În curs',
            'Durata (min)': durationMinutes,
            'Motiv': br.reason || 'N/A',
            'Status': br.isActive ? 'Activ' : 'Finalizat'
          });
        });
      }
    });

    if (breaksData.length > 0) {
      const breaksSheet = XLSX.utils.json_to_sheet(breaksData);
      breaksSheet['!cols'] = [
        { width: 20 }, // Nume
        { width: 12 }, // Data
        { width: 25 }, // Proiect
        { width: 25 }, // Locație
        { width: 18 }, // Început pauză
        { width: 18 }, // Sfârșit pauză
        { width: 12 }, // Durata
        { width: 30 }, // Motiv
        { width: 10 }  // Status
      ];
      XLSX.utils.book_append_sheet(workbook, breaksSheet, 'Pauze');
    }

    // Create material summary for final report
    const materialSummary: any[] = [];
    const userMaterialGroups: { [userName: string]: any } = {};
    
    // Group materials by user
    for (const entry of workEntries) {
      try {
        const materials = await materialConsumptionApi.getByWorkEntry(entry.id);
        const materialsArray = Array.isArray(materials) ? materials : [];
        
        const userName = entry.userName || 'N/A';
        if (!userMaterialGroups[userName]) {
          userMaterialGroups[userName] = {};
        }
        
        materialsArray.forEach(material => {
          const materialType = material.materialTypeName || 'N/A';
          if (!userMaterialGroups[userName][materialType]) {
            userMaterialGroups[userName][materialType] = {
              totalQuantity: 0,
              unit: material.unitSymbol || material.unitName || '',
              occurrences: 0
            };
          }
          userMaterialGroups[userName][materialType].totalQuantity += material.quantity || 0;
          userMaterialGroups[userName][materialType].occurrences += 1;
        });
      } catch (error) {
        console.warn(`Error loading materials for summary entry ${entry.id}:`, error);
      }
    }
    
    // Create summary rows
    Object.entries(userMaterialGroups).forEach(([userName, materials]) => {
      const materialSummaryText = Object.entries(materials).map(([materialType, data]: [string, any]) => 
        `${materialType}: ${data.totalQuantity.toLocaleString('ro-RO')} ${data.unit}${data.occurrences > 1 ? ` (${data.occurrences}x)` : ''}`
      ).join('; ');
      
      materialSummary.push({
        'Nume': userName,
        'Materiale utilizate': materialSummaryText || 'Fără materiale'
      });
    });
    
    // Add additional materials from wizard if provided
    if (additionalMaterials?.supervisorMaterials && additionalMaterials.supervisorMaterials.length > 0) {
      const supervisorMaterialsText = additionalMaterials.supervisorMaterials.map(material =>
        `${material.materialType}: ${material.quantity} ${material.unit}`
      ).join('; ');
      
      materialSummary.push({
        'Nume': 'Supervisor (Manual)',
        'Materiale utilizate': supervisorMaterialsText
      });
    }
    
    if (additionalMaterials?.teamMaterials && additionalMaterials.teamMaterials.length > 0) {
      const teamMaterialsText = additionalMaterials.teamMaterials.map(material =>
        `${material.materialType}: ${material.quantity} ${material.unit}`
      ).join('; ');
      
      materialSummary.push({
        'Nume': 'Echipa (Manual)',
        'Materiale utilizate': teamMaterialsText
      });
    }
    
    if (materialSummary.length > 0) {
      const materialSummarySheet = XLSX.utils.json_to_sheet(materialSummary);
      materialSummarySheet['!cols'] = [
        { width: 20 }, // Nume
        { width: 60 }  // Materiale utilizate
      ];
      XLSX.utils.book_append_sheet(workbook, materialSummarySheet, 'Sumar Materiale');
    }
  }

  XLSX.writeFile(workbook, filename);
}

/**
 * Export regenerated passwords for supervisors and monitors to Excel
 */
export async function exportRegeneratedPasswordsToExcel(
  regeneratedUsers: Array<{name: string, role: string, newPassword: string}>, 
  filename: string
) {
  const workbook = XLSX.utils.book_new();
  
  // Prepare data for Excel with Romanian headers
  const passwordData = regeneratedUsers.map(user => ({
    'Nume': user.name,
    'Rol': user.role === 'supervisor' ? 'Supervisor' : 
           user.role === 'monitor' ? 'Monitor' : user.role,
    'Nouă Parolă': user.newPassword,
    'Data Generării': new Date().toLocaleDateString('ro-RO'),
    'Observații': 'Parolă generată automat - trebuie schimbată la primul login'
  }));

  const sheet = XLSX.utils.json_to_sheet(passwordData);
  
  // Set column widths for better formatting
  sheet['!cols'] = [
    { width: 25 }, // Nume
    { width: 15 }, // Rol
    { width: 20 }, // Nouă Parolă
    { width: 15 }, // Data Generării
    { width: 40 }  // Observații
  ];

  // Add styling
  const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
  
  // Style header row
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
    if (!sheet[cellAddress]) continue;
    sheet[cellAddress].s = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "4472C4" } },
      alignment: { horizontal: "center" }
    };
  }

  XLSX.utils.book_append_sheet(workbook, sheet, 'Parole Regenerate');

  // Write the file
  XLSX.writeFile(workbook, filename);
}

/**
 * Export organizational structure hierarchy to Excel
 */
export async function exportOrganizationalStructureToExcel(
  teams: Array<{
    id: number;
    name: string;
    supervisorId: number;
    monitorId: number;
    members: Array<{
      id: number;
      name: string;
      role: string;
      workerType?: string;
    }>;
  }>,
  users: Array<{
    id: number;
    name: string;
    role: string;
    workerType?: string;
    teamId?: number;
    supervisorId?: number;
    monitorId?: number;
  }>,
  filename: string
) {
  const workbook = XLSX.utils.book_new();

  // Helper function to find user by ID
  const findUser = (id: number) => users.find(u => u.id === id);

  // 1. Organizational Hierarchy Sheet
  const hierarchyData: any[] = [];
  
  // Add standalone supervisors and monitors (not in teams)
  const supervisorsInTeams = new Set(teams.map(t => t.supervisorId));
  const monitorsInTeams = new Set(teams.map(t => t.monitorId));
  
  const standaloneSupervisors = users.filter(u => 
    u.role === 'supervisor' && !supervisorsInTeams.has(u.id)
  );
  const standaloneMonitors = users.filter(u => 
    u.role === 'monitor' && !monitorsInTeams.has(u.id)
  );

  // Add standalone roles first
  standaloneSupervisors.forEach(supervisor => {
    hierarchyData.push({
      'Nivel': 'Supervisor Independent',
      'Nume': supervisor.name,
      'Rol': 'Supervisor',
      'Tip Lucrător': supervisor.workerType || 'N/A',
      'Echipa': 'Nu este asignat unei echipe',
      'Supervisor': '-',
      'Monitor': '-',
      'ID': supervisor.id,
      'Observații': 'Supervisor fără echipă asignată'
    });
  });

  standaloneMonitors.forEach(monitor => {
    hierarchyData.push({
      'Nivel': 'Monitor Independent',
      'Nume': monitor.name,
      'Rol': 'Monitor',
      'Tip Lucrător': monitor.workerType || 'N/A',
      'Echipa': 'Nu este asignat unei echipe',
      'Supervisor': '-',
      'Monitor': '-',
      'ID': monitor.id,
      'Observații': 'Monitor fără echipă asignată'
    });
  });

  // Add teams hierarchy
  teams.forEach(team => {
    const supervisor = findUser(team.supervisorId);
    const monitor = findUser(team.monitorId);

    // Team header
    hierarchyData.push({
      'Nivel': '🏢 ECHIPA',
      'Nume': team.name,
      'Rol': 'Echipă',
      'Tip Lucrător': '-',
      'Echipa': team.name,
      'Supervisor': supervisor?.name || 'Necunoscut',
      'Monitor': monitor?.name || 'Necunoscut',
      'ID': team.id,
      'Observații': `Echipă cu ${team.members.length} membri`
    });

    // Supervisor
    if (supervisor) {
      hierarchyData.push({
        'Nivel': '├── Supervisor',
        'Nume': supervisor.name,
        'Rol': 'Supervisor',
        'Tip Lucrător': supervisor.workerType || 'N/A',
        'Echipa': team.name,
        'Supervisor': supervisor.name,
        'Monitor': monitor?.name || 'Necunoscut',
        'ID': supervisor.id,
        'Observații': 'Conducătorul echipei'
      });
    }

    // Monitor
    if (monitor) {
      hierarchyData.push({
        'Nivel': '├── Monitor',
        'Nume': monitor.name,
        'Rol': 'Monitor',
        'Tip Lucrător': monitor.workerType || 'N/A',
        'Echipa': team.name,
        'Supervisor': supervisor?.name || 'Necunoscut',
        'Monitor': monitor.name,
        'ID': monitor.id,
        'Observații': 'Monitorizează echipa'
      });
    }

    // Team members
    team.members.forEach((member, index) => {
      const isLast = index === team.members.length - 1;
      hierarchyData.push({
        'Nivel': isLast ? '└── Membru' : '├── Membru',
        'Nume': member.name,
        'Rol': member.role === 'worker' ? 'Muncitor' : member.role,
        'Tip Lucrător': member.workerType || 'N/A',
        'Echipa': team.name,
        'Supervisor': supervisor?.name || 'Necunoscut',
        'Monitor': monitor?.name || 'Necunoscut',
        'ID': member.id,
        'Observații': 'Membru al echipei'
      });
    });

    // Add spacing between teams
    hierarchyData.push({
      'Nivel': '',
      'Nume': '',
      'Rol': '',
      'Tip Lucrător': '',
      'Echipa': '',
      'Supervisor': '',
      'Monitor': '',
      'ID': '',
      'Observații': ''
    });
  });

  // Add unassigned workers
  const workersInTeams = new Set(teams.flatMap(t => t.members.map(m => m.id)));
  const unassignedWorkers = users.filter(u => 
    u.role === 'worker' && !workersInTeams.has(u.id)
  );

  if (unassignedWorkers.length > 0) {
    hierarchyData.push({
      'Nivel': '🚫 MUNCITORI NEASIGNAȚI',
      'Nume': `${unassignedWorkers.length} muncitori`,
      'Rol': 'Grup',
      'Tip Lucrător': '-',
      'Echipa': 'Fără echipă',
      'Supervisor': '-',
      'Monitor': '-',
      'ID': '-',
      'Observații': 'Muncitori care nu sunt asignați unei echipe'
    });

    unassignedWorkers.forEach((worker, index) => {
      const isLast = index === unassignedWorkers.length - 1;
      hierarchyData.push({
        'Nivel': isLast ? '└── Neasignat' : '├── Neasignat',
        'Nume': worker.name,
        'Rol': 'Muncitor',
        'Tip Lucrător': worker.workerType || 'N/A',
        'Echipa': 'Fără echipă',
        'Supervisor': '-',
        'Monitor': '-',
        'ID': worker.id,
        'Observații': 'Nu este asignat unei echipe'
      });
    });
  }

  const hierarchySheet = XLSX.utils.json_to_sheet(hierarchyData);
  hierarchySheet['!cols'] = [
    { width: 20 }, // Nivel
    { width: 25 }, // Nume
    { width: 15 }, // Rol
    { width: 15 }, // Tip Lucrător
    { width: 20 }, // Echipa
    { width: 20 }, // Supervisor
    { width: 20 }, // Monitor
    { width: 8 },  // ID
    { width: 35 }  // Observații
  ];

  XLSX.utils.book_append_sheet(workbook, hierarchySheet, 'Structură Organizațională');

  // 2. Teams Summary Sheet
  const teamsSummaryData = teams.map(team => {
    const supervisor = findUser(team.supervisorId);
    const monitor = findUser(team.monitorId);
    const workerCount = team.members.filter(m => m.role === 'worker').length;
    const angajatiCount = team.members.filter(m => m.workerType === 'Angajat').length;
    const colaboratoriCount = team.members.filter(m => m.workerType === 'Colaborator').length;

    return {
      'Nume Echipă': team.name,
      'ID Echipă': team.id,
      'Supervisor': supervisor?.name || 'Necunoscut',
      'Monitor': monitor?.name || 'Necunoscut',
      'Total Membri': team.members.length,
      'Muncitori': workerCount,
      'Angajați': angajatiCount,
      'Colaboratori': colaboratoriCount,
      'Alți Membri': team.members.length - workerCount,
      'Status': team.members.length > 0 ? 'Activă' : 'Goală',
      'Data Creării': new Date().toLocaleDateString('ro-RO'),
      'Observații': `Echipă cu ${team.members.length} membri`
    };
  });

  const teamsSummarySheet = XLSX.utils.json_to_sheet(teamsSummaryData);
  teamsSummarySheet['!cols'] = [
    { width: 25 }, // Nume Echipă
    { width: 10 }, // ID Echipă
    { width: 20 }, // Supervisor
    { width: 20 }, // Monitor
    { width: 12 }, // Total Membri
    { width: 12 }, // Muncitori
    { width: 12 }, // Angajați
    { width: 12 }, // Colaboratori
    { width: 12 }, // Alți Membri
    { width: 10 }, // Status
    { width: 15 }, // Data Creării
    { width: 35 }  // Observații
  ];

  XLSX.utils.book_append_sheet(workbook, teamsSummarySheet, 'Sumar Echipe');

  // 3. Supervisors & Monitors Sheet
  const supervisorsAndMonitors = users.filter(u => 
    u.role === 'supervisor' || u.role === 'monitor'
  );

  const supervisionData = supervisorsAndMonitors.map(user => {
    const managedTeams = teams.filter(t => 
      t.supervisorId === user.id || t.monitorId === user.id
    );
    const totalManagedMembers = managedTeams.reduce((total, team) => total + team.members.length, 0);

    return {
      'Nume': user.name,
      'Rol': user.role === 'supervisor' ? 'Supervisor' : 'Monitor',
      'Tip Lucrător': user.workerType || 'N/A',
      'ID': user.id,
      'Echipe Gestionate': managedTeams.length,
      'Nume Echipe': managedTeams.map(t => t.name).join(', ') || 'Nicio echipă',
      'Total Membri Gestionați': totalManagedMembers,
      'Status': managedTeams.length > 0 ? 'Activ' : 'Inactiv',
      'Responsabilități': user.role === 'supervisor' ? 
        'Conducerea echipei, gestionarea muncitorilor' : 
        'Monitorizarea echipei, raportarea',
      'Observații': managedTeams.length === 0 ? 
        'Nu are echipe asignate' : 
        `Gestionează ${managedTeams.length} echipe`
    };
  });

  const supervisionSheet = XLSX.utils.json_to_sheet(supervisionData);
  supervisionSheet['!cols'] = [
    { width: 25 }, // Nume
    { width: 15 }, // Rol
    { width: 15 }, // Tip Lucrător
    { width: 8 },  // ID
    { width: 15 }, // Echipe Gestionate
    { width: 30 }, // Nume Echipe
    { width: 20 }, // Total Membri Gestionați
    { width: 10 }, // Status
    { width: 35 }, // Responsabilități
    { width: 30 }  // Observații
  ];

  XLSX.utils.book_append_sheet(workbook, supervisionSheet, 'Supervisori și Monitori');

  // 4. All Users Sheet
  const allUsersData = users.map(user => {
    const userTeam = teams.find(t => 
      t.members.some(m => m.id === user.id) || 
      t.supervisorId === user.id || 
      t.monitorId === user.id
    );

    return {
      'ID': user.id,
      'Nume': user.name,
      'Email': user.email || 'N/A',
      'Telefon': user.phoneNumber || 'N/A',
      'Rol': user.role === 'worker' ? 'Muncitor' : 
             user.role === 'supervisor' ? 'Supervisor' :
             user.role === 'monitor' ? 'Monitor' :
             user.role === 'admin' ? 'Administrator' : user.role,
      'Tip Lucrător': user.workerType || 'N/A',
      'Echipa': userTeam?.name || 'Fără echipă',
      'ID Echipă': userTeam?.id || '-',
      'Supervisor în Echipă': userTeam && userTeam.supervisorId === user.id ? 'Da' : 'Nu',
      'Monitor în Echipă': userTeam && userTeam.monitorId === user.id ? 'Da' : 'Nu',
      'Status Membru': userTeam ? 
        (userTeam.supervisorId === user.id ? 'Supervisor' :
         userTeam.monitorId === user.id ? 'Monitor' : 'Membru') : 'Neasignat',
      'Data Înregistrării': new Date().toLocaleDateString('ro-RO')
    };
  });

  const allUsersSheet = XLSX.utils.json_to_sheet(allUsersData);
  allUsersSheet['!cols'] = [
    { width: 8 },  // ID
    { width: 25 }, // Nume
    { width: 30 }, // Email
    { width: 20 }, // Telefon
    { width: 15 }, // Rol
    { width: 15 }, // Tip Lucrător
    { width: 20 }, // Echipa
    { width: 10 }, // ID Echipă
    { width: 15 }, // Supervisor în Echipă
    { width: 15 }, // Monitor în Echipă
    { width: 15 }, // Status Membru
    { width: 15 }  // Data Înregistrării
  ];

  XLSX.utils.book_append_sheet(workbook, allUsersSheet, 'Toți Utilizatorii');

  // 5. Statistics Sheet
  const totalUsers = users.length;
  const totalTeams = teams.length;
  const totalWorkers = users.filter(u => u.role === 'worker').length;
  const totalSupervisors = users.filter(u => u.role === 'supervisor').length;
  const totalMonitors = users.filter(u => u.role === 'monitor').length;
  const totalAngajati = users.filter(u => u.workerType === 'Angajat').length;
  const totalColaboratori = users.filter(u => u.workerType === 'Colaborator').length;
  const unassignedUsersCount = users.filter(u => !teams.some(t => 
    t.members.some(m => m.id === u.id) || t.supervisorId === u.id || t.monitorId === u.id
  )).length;

  const statsData = [
    { 'Statistică': 'Total Utilizatori', 'Valoare': totalUsers, 'Procent': '100%' },
    { 'Statistică': 'Total Echipe', 'Valoare': totalTeams, 'Procent': `${((totalTeams / Math.max(totalUsers, 1)) * 100).toFixed(1)}%` },
    { 'Statistică': 'Total Muncitori', 'Valoare': totalWorkers, 'Procent': `${((totalWorkers / totalUsers) * 100).toFixed(1)}%` },
    { 'Statistică': 'Total Supervisori', 'Valoare': totalSupervisors, 'Procent': `${((totalSupervisors / totalUsers) * 100).toFixed(1)}%` },
    { 'Statistică': 'Total Monitori', 'Valoare': totalMonitors, 'Procent': `${((totalMonitors / totalUsers) * 100).toFixed(1)}%` },
    { 'Statistică': 'Angajați', 'Valoare': totalAngajati, 'Procent': `${((totalAngajati / totalUsers) * 100).toFixed(1)}%` },
    { 'Statistică': 'Colaboratori', 'Valoare': totalColaboratori, 'Procent': `${((totalColaboratori / totalUsers) * 100).toFixed(1)}%` },
    { 'Statistică': 'Utilizatori Neasignați', 'Valoare': unassignedUsersCount, 'Procent': `${((unassignedUsersCount / totalUsers) * 100).toFixed(1)}%` },
    { 'Statistică': 'Echipe Active', 'Valoare': teams.filter(t => t.members.length > 0).length, 'Procent': `${((teams.filter(t => t.members.length > 0).length / Math.max(totalTeams, 1)) * 100).toFixed(1)}%` },
    { 'Statistică': 'Echipe Goale', 'Valoare': teams.filter(t => t.members.length === 0).length, 'Procent': `${((teams.filter(t => t.members.length === 0).length / Math.max(totalTeams, 1)) * 100).toFixed(1)}%` },
    { 'Statistică': 'Raport Supervisor/Muncitor', 'Valoare': totalWorkers > 0 ? (totalWorkers / Math.max(totalSupervisors, 1)).toFixed(1) : '0', 'Procent': 'ratio' },
    { 'Statistică': 'Raport Monitor/Echipă', 'Valoare': totalTeams > 0 ? (totalTeams / Math.max(totalMonitors, 1)).toFixed(1) : '0', 'Procent': 'ratio' }
  ];

  const statsSheet = XLSX.utils.json_to_sheet(statsData);
  statsSheet['!cols'] = [
    { width: 30 }, // Statistică
    { width: 15 }, // Valoare
    { width: 15 }  // Procent
  ];

  XLSX.utils.book_append_sheet(workbook, statsSheet, 'Statistici');

  // Apply header styling to all sheets
  [hierarchySheet, teamsSummarySheet, supervisionSheet, allUsersSheet, statsSheet].forEach(sheet => {
    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!sheet[cellAddress]) continue;
      sheet[cellAddress].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "2563eb" } },
        alignment: { horizontal: "center" }
      };
    }
  });

  // Write the file
  XLSX.writeFile(workbook, filename);
}